import React, { useEffect, useState } from "react";
import "../styles/components/headerLoggedIn.css";
import Profile from "./Profile";
import { useAuth } from "../context/AuthContext";
import { ChevronDown, ContactRound, LogOut } from "lucide-react";
import { ProfilesService, ProfileResponseDto } from "../services/profilesService";
import { toAbsoluteUrl } from "../config/api";

const HeaderLoggedIn: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [sideOpen, setSideOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false); // Thêm trạng thái cho form xác nhận
  const { user, logout } = useAuth();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [profile, setProfile] = useState<ProfileResponseDto | null>(null);

  // Sử dụng user từ context, chỉ fallback khi không có user
  const displayUser = user;

  const getInitials = (nameOrEmail?: string) => {
    const src = (nameOrEmail || '').trim();
    if (!src) return 'U';
    const parts = src.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    const single = parts[0];
    const base = single && single.includes('@') ? single.split('@')[0] : single;
    return (base?.[0] || 'U').toUpperCase();
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const p = await ProfilesService.getMyProfile();
        if (!mounted) return;
        setProfile(p);
        if (p && p.avatar) {
          setAvatarUrl(toAbsoluteUrl(p.avatar));
        } else {
          setAvatarUrl(null);
        }
      } catch {
        setProfile(null);
        setAvatarUrl(null);
      }
    };
    if (displayUser) load();
    return () => { mounted = false; };
  }, [displayUser]);

  // Refresh profile data when side panel closes (in case profile was updated)
  useEffect(() => {
    if (!sideOpen && displayUser) {
      const refreshProfile = async () => {
        try {
          const p = await ProfilesService.getMyProfile();
          setProfile(p);
          if (p && p.avatar) {
            setAvatarUrl(toAbsoluteUrl(p.avatar));
          } else {
            setAvatarUrl(null);
          }
        } catch {
          // Ignore errors on refresh
        }
      };
      refreshProfile();
    }
  }, [sideOpen, displayUser]);

  return (
    <>
      <header className="header-logged-in">
        <div className="header-title">Dashboard</div>

        {displayUser && (
          <div className="header-user">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={profile?.fullName || displayUser.fullName}
                className="header-avatar"
              />
            ) : (
              <div
                className="header-avatar"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#e0e7ff',
                  color: '#3730a3',
                  fontWeight: 600,
                  fontSize: 14,
                  userSelect: 'none'
                }}
                title={profile?.fullName || displayUser.fullName || displayUser.email}
              >
                {getInitials(profile?.fullName || displayUser.fullName || displayUser.email)}
              </div>
            )}

            <div className="header-info">
              <span className="header-name">{profile?.fullName || displayUser.fullName}</span>
              <span className="header-role">
                {displayUser.role === 'admin' ? 'Quản trị viên' : 
                 displayUser.role === 'moderator' ? 'Kiểm duyệt viên' :
                 displayUser.role === 'premium' ? 'Thành viên Premium' :
                 displayUser.role === 'tool_user' ? 'Tool User' :
                 displayUser.role === 'member' ? 'Thành viên' :
                 displayUser.role === 'guest' ? 'Khách' :
                 displayUser.role}
              </span>
            </div>

            <button
              className="header-logout"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <ChevronDown size={16} />
            </button>

            {menuOpen && (
              <div className="dropdown-menu">
                <div
                  className="menu-item"
                  onClick={() => {
                    setSideOpen(true);
                    setMenuOpen(false);
                  }}
                >
                  <ContactRound size={16} /> Hồ sơ cá nhân
                </div>
                <div
                  className="menu-item"
                  onClick={() => {
                    setShowLogoutConfirm(true);
                  }}
                >
                  <LogOut size={16} /> Đăng xuất
                </div>
              </div>
            )}
          </div>
        )}
      </header>

      {/* Overlay */}
      {sideOpen && (
        <div className="overlay" onClick={() => setSideOpen(false)} />
      )}

      {/* Side Panel */}
      {sideOpen && <Profile onClose={() => setSideOpen(false)} />}

      {/* Form xác nhận đăng xuất */}
      {showLogoutConfirm && (
        <div className="logout-confirm-overlay">
          <div className="logout-confirm">
            <h3>Xác nhận đăng xuất</h3>
            <p>Bạn có chắc chắn muốn đăng xuất không?</p>
            <div className="confirm-actions">
              <button
                className="confirm-btn cancel"
                onClick={() => setShowLogoutConfirm(false)}
              >
                Hủy
              </button>
              <button
                className="confirm-btn confirm"
                onClick={() => {
                  logout();
                  setShowLogoutConfirm(false);
                  setMenuOpen(false); 
                }}
              >
                Xác nhận
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HeaderLoggedIn;